import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useOpportunities } from './useOpportunities';
import { useAccounts } from './useAccounts';
import { useContacts } from './useContacts';
import { useDocuments } from './useDocuments';
import { useRouter } from 'vue-router';
import { CustomEventNames } from '@apparatix/constants/CustomEventNames';

type CrmPage = 'Leads' | 'Accounts' | 'Contacts' | 'Opportunities' | 'Documents' | 'Activities';
export type CrmDetailTabName = 'overview' | 'relationships' | 'deals' | 'activity' | 'files';

export const useCrm = () => {
  const opportunityStore = useOpportunityStore();
  const { loadOpportunities } = useOpportunities();
  const { loadAccountsHome } = useAccounts();
  const { loadContactsHome } = useContacts();
  const { loadDocumentsHome } = useDocuments();

  const router = useRouter();

  const reloadOpportunitiesHome = async () => {
    opportunityStore.protectMasterList = false;
    await loadOpportunities('Opportunity', 'high');
    opportunityStore.protectMasterList = true;
  };

  const reloadLeadsHome = async () => {
    opportunityStore.protectMasterList = false;
    await loadOpportunities('Lead', 'high');
    opportunityStore.protectMasterList = true;
  };

  const reloadActivityHome = () => {
    // Since activities home doesn't use a store, and has a complicated initialization, we just fire an event and let the page itself handle it
    const reloadEvent = new CustomEvent(CustomEventNames.crm.activities.reloadActivityHome, {
      bubbles: true,
      cancelable: true,
    });

    window.dispatchEvent(reloadEvent);
  };

  const handleSubpageItemClick = (newPage: string, currentPage: string) => {
    if (newPage.toLowerCase() !== currentPage.toLowerCase()) return;
    handleCrmPageRefresh(currentPage);
  };

  const handleCrmPageRefresh = async (page: string) => {
    switch (page as CrmPage) {
      case 'Leads':
        await reloadLeadsHome();
        break;
      case 'Accounts':
        await loadAccountsHome(true);
        break;
      case 'Contacts':
        loadContactsHome(true);
        break;
      case 'Opportunities':
        await reloadOpportunitiesHome();
        break;
      case 'Documents':
        loadDocumentsHome(true);
        break;
      case 'Activities':
        reloadActivityHome();
        break;
      default:
        return;
    }
  };

  const handleDetailTabUpdate = (newTabValue: CrmDetailTabName, refUpdater: () => void) => {
    router.replace({ params: { activeTab: newTabValue }});
    refUpdater();
  };

  const getCrmDetailTabList = () => [
    'overview',
    'relationships',
    'deals',
    'activity',
    'files',
    'profile',
  ];

  // arguably contains superfluous check given parameter type hinting
  const isDetailTab = (value: string): boolean => typeof value === 'string' && getCrmDetailTabList().includes(value.toLowerCase());

  return {
    handleCrmPageRefresh,
    handleSubpageItemClick,
    handleDetailTabUpdate,
    isDetailTab,
  };
};
